/**
 * Randomness for the classroom tools.
 *
 * Every function takes its source of randomness as an argument, so the widgets pass
 * `secureRandom` from an event handler (never during render — the first render is
 * static HTML) and the tests pass `seededRandom` to get the same layout every run.
 */

const UINT32_RANGE = 4_294_967_296;
const PARK_MILLER_MODULUS = 2_147_483_647;
const PARK_MILLER_MULTIPLIER = 48_271;

/** A uniform number in [0, 1) from the platform CSPRNG. Browser event handlers only. */
export function secureRandom(): number {
  const buffer = new Uint32Array(1);
  globalThis.crypto.getRandomValues(buffer);
  return (buffer[0] ?? 0) / UINT32_RANGE;
}

/**
 * A small deterministic generator (Park–Miller minimal standard) for tests and for
 * reproducible layouts. Plain arithmetic, no bit operations, exact in doubles.
 */
export function seededRandom(seed: number): () => number {
  let state = Math.abs(Math.floor(seed)) % PARK_MILLER_MODULUS;
  if (state === 0) {
    state = 1;
  }
  return () => {
    state = (state * PARK_MILLER_MULTIPLIER) % PARK_MILLER_MODULUS;
    return (state - 1) / (PARK_MILLER_MODULUS - 1);
  };
}

/** An integer in [0, length). */
export function randomIndex(length: number, random: () => number): number {
  return Math.min(length - 1, Math.floor(random() * length));
}

/** Fisher–Yates shuffle into a new array. */
export function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1, random);
    const held = result[i] as T;
    result[i] = result[j] as T;
    result[j] = held;
  }
  return result;
}
